import { createSelector } from '@reduxjs/toolkit'
import { selectUser } from './userSlice'

/**
 * The ACTIVE permission set: a spoofed user's permissions take precedence over the real user's
 * (spoofing impersonates a — usually lower-privilege — user). This rule was previously inlined as
 * `active.spoofed_user.user_permission_options ? ... : active.data.user_permission_options` across
 * the module-system tables/lists; it now lives here, defined once.
 *
 * - `getActivePermissions(user)` — pure form for the param-passed `valueColumn` sites and the
 *   effects/callbacks that can't call a hook.
 * - `selectActivePermissions` — the memoized selector for component/hook contexts.
 *
 * Use `??` (nullish), NOT `||`: an empty spoofed array `[]` is a real "this spoofed user has no
 * permissions" signal and must be honored, not fall through to the real user's perms.
 *
 * Do NOT fold this into `hasUserPermission` — the REAL user's perms are still needed in places
 * (e.g. `can_spoof_admin`); see docs/PERMISSIONS.md.
 */
export const getActivePermissions = (user) =>
  user?.spoofed_user?.user_permission_options ?? user?.data?.user_permission_options ?? []

export const selectActivePermissions = createSelector([selectUser], getActivePermissions)
